import { ActionsEnum } from './actions';

/**
 * @typedef {Object} Action
 * @property {string} type
 * @property {string|boolean|number} payload
 */

/**
 * @typedef {Object} TTSState
 * @property {boolean} isTTSAvailable - IS text-to-speech available at all
 * @property {boolean} isSpeaking - Is TTS currently speaking
 * @property {boolean} isPaused - Is TTS currently paused
 * @property {boolean} hasAnyText - Are there any words on the current page to read
 * @property {boolean} hasPrevious - Are there any words before the current word being read
 * @property {boolean} hasNext - Are there any words after the current word being read
 * @property {boolean} isTTSDialogOpen - Is the TTS recommendation dialog open
 * @property {boolean} didClickTTSDialogFit - Did the user click "Fit to Screen" in the TTS dialog
 * @property {string|null} readFrom - The CFI to start reading from
 */

/**
 * @type {TTSState}
 */
export const ttsInitialState = {
  isTTSAvailable: false,
  isSpeaking: false,
  isPaused: false,
  hasAnyText: false,
  hasPrevious: false,
  hasNext: false,
  isTTSDialogOpen: false,
  didClickTTSDialogFit: false,
  readFrom: null,
};

/**
 * @param {TTSState} state
 * @param {Action} action
 * @returns {TTSState}
 */
export function ttsReducer(state, action) {
  const { type, payload } = action;
  switch (type) {
    // --------------------------------------------------
    // Atomic actions
    // --------------------------------------------------
    case ActionsEnum.SET_TTS_AVAILABLE:
      return { ...state, isTTSAvailable: payload };
    case ActionsEnum.SET_SPEAKING:
      return { ...state, isSpeaking: payload };
    case ActionsEnum.SET_PAUSED:
      return { ...state, isPaused: payload };
    case ActionsEnum.SET_HAS_ANY_TEXT:
      return { ...state, hasAnyText: payload };
    case ActionsEnum.SET_HAS_PREVIOUS:
      return { ...state, hasPrevious: payload };
    case ActionsEnum.SET_HAS_NEXT:
      return { ...state, hasNext: payload };
    case ActionsEnum.SET_TTS_DIALOG_OPEN:
      return { ...state, isTTSDialogOpen: payload };
    case ActionsEnum.SET_DID_CLICK_TTS_DIALOG_FIT:
      return { ...state, didClickTTSDialogFit: payload };
    case ActionsEnum.SET_READ_FROM:
      return { ...state, readFrom: payload };
    // --------------------------------------------------
    // Composite actions
    // --------------------------------------------------
    case ActionsEnum.SET_PREVIOUS_AND_NEXT: {
      const { hasPrevious, hasNext } = payload;
      return { ...state, hasPrevious, hasNext };
    }
    case ActionsEnum.START_READING:
      return { ...state, isSpeaking: true, isPaused: false };
    case ActionsEnum.PAUSE_READING:
      return { ...state, isSpeaking: false, isPaused: true };
    case ActionsEnum.STOP_READING:
      return { ...state, isSpeaking: false, isPaused: false };
    // --------------------------------------------------
    // Reset actions
    // --------------------------------------------------
    case ActionsEnum.RESET_STATE:
      return ttsInitialState;
    default:
      return state;
  }
}
